import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import type { AccountType } from "@shared/schema";

declare global {
  interface Window {
    $crisp: { push: (...args: unknown[]) => void };
    CRISP_WEBSITE_ID: string;
  }
}

interface AuthMeResponse {
  user: {
    email: string;
    fullName: string | null;
    name: string | null;
  };
  effectivePlan?: AccountType;
}

export function useCrispIdentity() {
  const { data } = useQuery<AuthMeResponse>({
    queryKey: ["/api/app/auth/me"],
  });

  // Tracks the email we've already pushed a server-side CRM sync for so we only
  // fire the (fire-and-forget) /api/app/crisp/sync POST once per session/user.
  const syncedEmailRef = useRef<string | null>(null);

  useEffect(() => {
    if (typeof window === "undefined" || !window.$crisp || typeof window.$crisp.push !== "function") return;

    // Move the launcher bubble to the bottom-left on every screen.
    window.$crisp.push(["config", "position:reverse", [true]]);

    if (data?.user) {
      const { email, fullName, name } = data.user;
      const plan = data.effectivePlan ?? "Host";
      const displayName = fullName || name || email;

      window.$crisp.push(["set", "user:email", [email]]);
      window.$crisp.push(["set", "user:nickname", [displayName]]);
      window.$crisp.push(["set", "session:data", [[["plan", plan]]]]);

      // Push the full customer record into Crisp People (CRM) once per user.
      // Fire-and-forget: the server responds 200 even when Crisp creds are
      // missing or the upstream call fails, so this never blocks the UI.
      if (syncedEmailRef.current !== email) {
        syncedEmailRef.current = email;
        void fetch("/api/app/crisp/sync", {
          method: "POST",
          credentials: "include",
        }).catch(() => {
          // Allow a retry on a later mount if the request itself fails.
          syncedEmailRef.current = null;
        });
      }
    } else {
      syncedEmailRef.current = null;
      window.$crisp.push(["do", "session:reset"]);
    }
  }, [data]);
}
